/**
 * 이상한 방 - 등급이 드러난 뒤의 세리머니 (시각표 후반부 · 순수 파생 · 연출 레이어)
 *
 * 근거 문서: 로드맵 확정안 3.3절(연출 시퀀스 · 열 장은 낮은 등급부터) · 0절 판정표 10번 · 6절("근접 실패 색 깜빡" 기각)
 *           `ui/juice.ts` 머리말(정리 권한은 타이머 하나 · `Math.random` 0)
 *
 * ## 시각표가 둘로 갈린 이유 - 긴장은 거짓 신호가 아니라 정보 부재에서 나온다
 *
 * 전에는 전 등급이 똑같이 2.0초였다. 등급이 결과에만 있고 시간에는 없었으니 SSR이 N과 같은 속도로 지나갔다.
 * 그렇다고 **개봉 전에** 등급을 흘리면 그건 긴장이 아니라 스포일러이고, 흘리는 척만 하는 것(바늘이 SSR 근처에서
 * 멎었다가 N으로 떨어지는 연출)은 로드맵 0절 10번과 6절이 이미 기각한 근접 실패 속임수다.
 *
 * 그래서 표를 둘로 갈랐다:
 *
 *  - `ENVELOPE_TIMELINE`(`envelopeTimeline.ts`) - **개봉 전.** 등급이 무엇이든 글자 하나까지 같다.
 *    길이도 소리도 떨림도 같고, 이 구간에서 등급을 아는 물건은 정직한 바늘과 릴, 그 둘이 가리키는 같은 값
 *    하나뿐이다(실제 `gradeRoll`).
 *  - `GRADE_CEREMONY`(이 파일) - **개봉 후.** 여기서부터 갈린다. 화려함은 예고가 아니라 **보상**이다.
 *
 * 계단은 0.35 / 0.7 / 1.5 / 2.84초다. N이 실제로 시시하고 짧아야 SSR이 대박으로 읽힌다 - 아래 값이
 * "후진 건 후지게"의 전부이고, N을 조금 더 화려하게 만들고 싶어지는 날 그 유혹을 이 주석이 막는다.
 *
 * ## 위 둘만 커졌다 (P5) - 그 대비가 규칙이다
 *
 * 사람의 말: "SR, SSR 뽑을 땐 진짜 염병 떠는 느낌으로 도파민 팡팡 터지는 오바 이펙트를 넣고 싶다."
 * 그래서 SR·SSR의 길이·파편·빛이 통째로 커졌고 SR에는 어두워짐이 새로 생겼다(전에는 SSR만 0이 아니었다).
 * **N과 R의 값은 한 자리도 바뀌지 않았다.** 밑을 함께 올리면 위가 커진 것이 아니라 전부가 커진 것이고,
 * 그러면 SSR이 도로 평범해진다 - 위를 키우는 값은 아래를 그대로 두는 값과 한 쌍이다.
 *
 * ## 열 장의 한 장도 이 표가 정한다 - 시각표는 더 이상 둘이 아니다
 *
 * 전에는 낮은 등급 아홉 장이 미니 칩으로 빠르게 뒤집혔고, 그 속도를 여기 있던 **두 번째 시각표**
 * (`TEN_PACE`의 가속 · `GradeCeremony.tenBeatMs`의 감속)가 정했다. 사람은 그것을 두고 "누르자마자 열 장
 * 결과가 바로 보인다"고 했다. 지금은 **아홉 장도 큰 카드 자리에서 한 장씩 개봉한다**(P5). 한 장의 길이는
 * 그 등급의 세리머니 하나(`stepRevealMs`)이므로 두 번째 표가 필요 없어졌고, 그래서 지웠다 - 남겨 두면
 * "열 장에서 한 장이 얼마나 걸리는가"가 두 곳에 산다.
 *
 * 순서는 `revealOrder`가 이미 낮은 것부터로 정렬해 두었다 - 마지막이 가장 좋은 것이라는 정직한 고조이지,
 * 순서를 속인 것이 아니다.
 *
 * ## 난수는 없다
 *
 * 파편의 자리·각도·지연은 **뽑기 결과에서 파생한다**(`shardsFor`가 `seq`와 `gradeRoll`로 `createRng`를 만든다).
 * `?pull=`로 같은 회차를 다시 열면 같은 그림이 나온다. `Math.random`은 이 화면 전체에서 0이다(`envelope.test.ts`).
 *
 * ## 이 파일에 `setTimeout`은 없다
 *
 * "언제"를 실제로 돌리는 것은 `envelopePhases.ts` 하나다(정리 권한은 타이머 하나). 여기 있는 것은 **표**와
 * **순수 파생**, 그리고 "이 등급으로 터뜨려라"를 받아 클래스를 얹는 레이어뿐이다(`envelopePhases.test.ts`).
 */

import { createRng } from '../core/rng'
import type { Grade } from '../game/economy/envelopeTable'

import { createElement } from './dom'

// 빛(어둠 · 광채 · 플래시)은 제 파일로 갈라져 나갔다 - 레이어를 만드는 자리가 둘 다 가져온다.
import './envelopeCeremony.css'
import './envelopeFlare.css'

/* --- 타입 ---------------------------------------------------------------------- */

/**
 * 빛의 종류. 색을 새로 만들지 않고 **노랑의 양과 밝기**로만 가른다(`envelope.css` 머리말의 등급 규칙 그대로).
 *  - `none` N. 빛이 없다
 *  - `sparkle` R. 흰 반짝임 한 번
 *  - `rays` SR. 도는 빛줄기 + 충격파 링 두 겹
 *  - `gold` SSR. 금빛 폭발 + 충격파 링 세 겹 + 빛기둥 + 연쇄 플래시 + 지속 오라
 *
 * **연출이 늘어도 필드는 늘지 않았다**(P5). 링이 몇 겹인가·기둥이 서는가·오라가 남는가는 전부 이 한 값이
 * 가른다 - 켤 것마다 불리언을 하나씩 더하면 표가 곧 "무엇을 켤 수 있는가"의 목록이 되고, 그때부터
 * `rays`와 `gold`가 무엇이 다른가를 표 한 줄로는 읽을 수 없다. 새 연출은 이 열거형에 붙는다.
 */
export type CeremonyFlare = 'none' | 'sparkle' | 'rays' | 'gold'

export type GradeCeremony = Readonly<{
  /** 등급이 드러나기 **직전** 화면이 어두워지는 시간. 0이면 어두워지지 않는다 - SR·SSR만 쓴다. */
  dimMs: number
  /** 봉인이 찢기고 카드가 뒤집히는 시간. 길수록 카드가 느리게 착지한다. */
  tearMs: number
  /** 이름·등급 스탬프가 앉는 시간. */
  stampMs: number
  /** 버튼이 나온 뒤의 숨 한 번. 예약하지 않고 총 길이에만 든다(`ceremonyTotalMs`). */
  restMs: number
  /** 흩날리는 조각 수. 0이면 파편이 없다. */
  shards: number
  /** 화면이 흔들리는가. */
  shake: boolean
  flare: CeremonyFlare
}>

/**
 * 파편 한 조각. 자리·각도·지연·크기 전부 뽑기 결과에서 파생한다(`shardsFor`).
 *
 * **꼭짓점이 따로 있는 이유는 중력이다**(P5). 전에는 시작과 끝 둘뿐이라 조각이 한 방향으로 곧게 날아가
 * 사라졌다 - 터진 것이 아니라 흩어진 것으로 보였다. 지금은 위로 솟았다(`apexY`) 아래로 떨어지므로
 * (`y`) 조각이 무게를 가진 물건이 된다. 곡선 자체는 CSS 키프레임의 세 눈금이 그린다.
 */
export type Shard = Readonly<{
  /** 카드 중심에서의 가로 이동(px). 끝점의 값이고 꼭짓점에서는 그 일부만 간다. */
  x: number
  /** 꼭짓점의 세로(px). **언제나 음수(위)**다. */
  apexY: number
  /** 끝점의 세로(px). 양수가 아래다 - 솟은 것보다 더 깊이 떨어진다. */
  y: number
  rotate: number
  delayMs: number
  scale: number
}>

export type EnvelopeCeremonyLayer = Readonly<{
  /** `.ev-ceremony`. 루트에 붙이는 것은 화면의 몫이다. */
  root: HTMLElement
  /** 첫 상태로. 어둠·빛·파편·흔들림을 전부 걷는다. */
  reset: () => void
  /** 화면이 어두워진다(SR·SSR). 등급이 드러나기 직전의 한 박자다. */
  dim: (plan: GradeCeremony) => void
  /** 등급이 드러난다. 빛·파편·흔들림이 여기서 한 번에 터진다. */
  burst: (grade: Grade, plan: GradeCeremony, shards: readonly Shard[]) => void
  /** 움직이지 않는 끝 상태(탭 건너뛰기 · 움직임 줄이기). 등급 색만 남고 아무것도 움직이지 않는다. */
  settle: (grade: Grade, plan: GradeCeremony) => void
}>

/* --- 표: 등급별 후반부 ----------------------------------------------------------- */

/**
 * 등급이 드러난 뒤의 시각표. **절대 시각이 아니라 구간 길이**다 - 전반부(`ENVELOPE_TIMELINE`) 끝에
 * 이어 붙는 값이라 "언제"가 아니라 "얼마나"가 맞다.
 *
 * | 등급 | 총 길이 | 무엇 |
 * |---|---|---|
 * | N | 0.35초 | 툭 열리고 카드가 힘없이 떨어진다. 빛도 파편도 없다 |
 * | R | 0.7초 | 반짝 한 번 + 카드가 또렷하게 선다 |
 * | SR | 1.5초 | 화면이 한 번 어두워지고 → 도는 빛줄기 + 충격파 링 두 겹 + 흔들림 + 파편 22조각 |
 * | SSR | 2.84초 | 길게 어두워지고 → 금빛이 터지며 링 세 겹·빛기둥이 서고 → 연쇄 플래시 + 파편 56조각 + 카드에 오라가 남는다 |
 *
 * **`dimMs`가 둘이 됐다**(P5). 전에는 SSR 하나만 어두워졌는데, SR의 화려함이 커지면서 "터지기 전의 한 박자"가
 * 없으면 빛이 그냥 켜지는 것으로 보였다. SR의 어둠은 SSR의 3분의 1이 안 된다 - 어둠의 **길이**가 여전히
 * 두 등급을 가르고, 어두워지는 동안에는 등급 색이 아직 DOM에 없다(`dim`의 주석).
 */
export const GRADE_CEREMONY: Readonly<Record<Grade, GradeCeremony>> = {
  common: { dimMs: 0, tearMs: 110, stampMs: 145, restMs: 95, shards: 0, shake: false, flare: 'none' },
  uncommon: { dimMs: 0, tearMs: 180, stampMs: 250, restMs: 270, shards: 0, shake: false, flare: 'sparkle' },
  rare: { dimMs: 120, tearMs: 300, stampMs: 460, restMs: 620, shards: 22, shake: true, flare: 'rays' },
  legend: { dimMs: 420, tearMs: 620, stampMs: 900, restMs: 900, shards: 56, shake: true, flare: 'gold' },
}

/**
 * 움직임 줄이기의 후반부. **전 등급이 같은 하나**다 - 길이 0에 빛도 파편도 흔들림도 없다.
 *
 * 값을 등급마다 두지 않는 것이 규칙이다. 움직임을 줄이겠다고 켠 설정에서 SSR만 조금 흔들리면
 * 그 설정은 지켜지지 않은 것이고, 등급마다 다른 "짧은 연출"을 두면 그 표가 또 하나의 시각표가 된다.
 */
export const REDUCED_CEREMONY: GradeCeremony = {
  dimMs: 0,
  tearMs: 0,
  stampMs: 0,
  restMs: 0,
  shards: 0,
  shake: false,
  flare: 'none',
}

/* --- 순수 계산 ------------------------------------------------------------------ */

/** 이 회차가 쓸 후반부 표. 움직임 줄이기면 등급과 무관하게 하나다. */
export function ceremonyFor(grade: Grade, reduced: boolean): GradeCeremony {
  return reduced ? REDUCED_CEREMONY : GRADE_CEREMONY[grade]
}

/** 후반부 총 길이. 어두워짐 + 찢김 + 스탬프 + 숨 한 번. */
export function ceremonyTotalMs(plan: GradeCeremony): number {
  return plan.dimMs + plan.tearMs + plan.stampMs + plan.restMs
}

/**
 * 아홉 장 구간에서 이 등급 카드 **한 장**이 차지하는 시간. 찢김 + 스탬프 + 숨 한 번이다.
 *
 * 어두워짐(`dimMs`)만 빠진다 - 화면을 어둡게 하는 것은 마지막 한 장(주인공)의 것이고, 아홉 장마다
 * 화면이 꺼졌다 켜지면 그건 고조가 아니라 깜빡임이다. 움직임 줄이기에서는 개봉 자체가 컷이라 0이다.
 */
export function stepRevealMs(grade: Grade, reduced: boolean): number {
  const plan = ceremonyFor(grade, reduced)
  return ceremonyTotalMs(plan) - plan.dimMs
}

/**
 * 아홉 장을 다 넘기는 총 시간. 순서는 `revealOrder`가 정한 그대로다.
 *
 * 화면(`envelopePhases.ts`)이 아니라 여기 있는 이유는 한 장의 길이가 이 파일의 표에서 나오기 때문이다.
 * 이 값을 쓰는 자리는 릴의 리드 스핀이다 - 앞의 아홉이 흐르는 동안 릴이 정확히 정수 바퀴를 돌아야
 * 감속이 시작되는 순간 트랙이 제자리에 있다(`envelopeReel.reelSpinPeriodMs`).
 */
export function tenStackTotalMs(grades: readonly Grade[], reduced: boolean): number {
  return grades.reduce((sum, grade) => sum + stepRevealMs(grade, reduced), 0)
}

/* --- 파편: 뽑기 결과에서 파생한다 ------------------------------------------------- */

/**
 * 파편의 폭·높이·회전·크기 편차. **P5에서 통째로 커졌다** - 조각 수가 8·18에서 22·56이 되면서
 * 전의 좁은 상자 안에 그만큼을 뿌리면 조각이 겹쳐 한 덩이로 읽혔다.
 *
 * 밖으로 나가는 조각이 생기는 것은 의도다. 레이어가 `overflow: hidden`이라 화면 가장자리에서 잘리고,
 * 그 잘림이 "판보다 큰 것이 터졌다"로 읽힌다 - 전부 안에 담기면 그건 폭발이 아니라 배치다.
 */
/** 가로로 퍼지는 폭(px). 카드 폭(240px)을 넘긴다 - 위 주석의 이유다. */
const SHARD_SPREAD_X = 190
/** 꼭짓점 높이의 하한·폭(px). 전부 위로 솟는다 - 터진 것은 먼저 올라간다. */
const SHARD_APEX_MIN = 90
const SHARD_APEX_SPAN = 130
/**
 * 떨어지는 깊이의 하한·폭(px). **하한이 꼭짓점의 상한보다 크다** - 그래서 어느 조각이든 솟은 높이보다
 * 깊이 떨어지고, 판 아래로 잘려 사라진다(`envelope.test.ts`가 그 부등호를 조각마다 검사한다).
 */
const SHARD_FALL_MIN = 240
const SHARD_FALL_SPAN = 180
/** 회전 폭(도). 한 바퀴 가까이 돈다. */
const SHARD_SPIN_DEG = 340
/** 흩어지는 지연 폭(ms). 전부 같은 순간에 튀면 파편이 아니라 판 하나가 된다. */
const SHARD_DELAY_MS = 190
/** 크기 편차. 하한을 낮추고 폭을 넓혀 큰 조각과 먼지가 함께 난다 - 편차가 곧 밀도로 읽힌다. */
const SHARD_SCALE_MIN = 0.42
const SHARD_SCALE_SPAN = 1.15

/** `gradeRoll` 실수를 시드 문자열에 넣을 때의 자릿수. 화면의 바늘(`toFixed(4)`)보다 촘촘하다. */
const SHARD_SEED_DIGITS = 6

/**
 * 이 회차의 파편들. **난수를 만들지 않고 뽑기 결과에서 파생한다** - 시드는 회차 번호와 등급 롤이라
 * 같은 회차를 다시 열면(`?pull=`) 같은 그림이 나온다. `Math.random`을 한 번이라도 쓰면 그 예외가 자란다
 * (`juice.ts` 머리말 규칙 3).
 */
export function shardsFor(seq: number, gradeRoll: number, count: number): readonly Shard[] {
  if (count <= 0) return []
  const rng = createRng(`envelope:shard:${seq}:${gradeRoll.toFixed(SHARD_SEED_DIGITS)}`)
  const shards: Shard[] = []
  for (let i = 0; i < count; i += 1) {
    shards.push({
      x: Math.round((rng.next() * 2 - 1) * SHARD_SPREAD_X),
      apexY: -Math.round(SHARD_APEX_MIN + rng.next() * SHARD_APEX_SPAN),
      y: Math.round(SHARD_FALL_MIN + rng.next() * SHARD_FALL_SPAN),
      rotate: Math.round((rng.next() * 2 - 1) * SHARD_SPIN_DEG),
      delayMs: Math.round(rng.next() * SHARD_DELAY_MS),
      scale: Number((SHARD_SCALE_MIN + rng.next() * SHARD_SCALE_SPAN).toFixed(2)),
    })
  }
  return shards
}

/* --- 레이어 -------------------------------------------------------------------- */

/**
 * 미리 만들어 두는 조각 노드 수. 가장 많이 쓰는 등급(SSR)에 맞춘다 - 연출 중에 노드를 만들지 않는다.
 * **표에서 파생한다.** 손으로 적으면 표를 키운 날 조각이 옛 개수에서 잘린다.
 */
const SHARD_NODES = GRADE_CEREMONY.legend.shards

/**
 * 충격파 링·빛기둥의 노드 수. **몇 개를 만드는가는 여기가, 몇 개를 켜는가는 `flare`가 정한다** -
 * `rays`는 링 두 겹, `gold`는 세 겹에 기둥까지다(`envelopeCeremony.css`). 조각과 같은 이유로 미리 만든다:
 * 연출 한복판에 노드를 만들면 그 프레임이 통째로 늦는다.
 *
 * 기둥이 홀수인 것은 가운데 한 가닥이 카드 정중앙에 서야 하기 때문이다(자리는 CSS의 `nth-child`이고,
 * 카드와 겹치는 구간은 그쪽에서 뚫는다 - 이 레이어는 카드 위에 얹힌다).
 */
const RING_NODES = 3
const PILLAR_NODES = 5

/**
 * 세리머니 레이어를 만든다. 붙이지 않는다 - 어디에 앉는지는 화면(`envelope.ts`)이 정한다.
 *
 * `host`는 `.ev-root`, `anchor`는 카드(`.ev-envelope`)다. 어둠·흔들림은 레이어 하나가 아니라 **화면 전체**의
 * 일이라 호스트에 클래스로 얹는다(`.ev-root.is-shake .ev-body`). 대신 클래스를 얹고 걷는 자리는 이 파일
 * 하나로 묶어 두었다 - 갈라 두면 "누가 `is-dim`을 떼는가"가 두 곳에 생긴다.
 *
 * **빛과 조각의 중심은 화면 한가운데가 아니라 카드다.** 이 화면의 카드는 위쪽에 앉으므로 화면 중심에서
 * 터뜨리면 빛의 구멍이 카드 아래에 생기고, 조각이 카드가 아니라 허공에서 튄다(실제로 그렇게 보였다).
 * 그래서 터뜨리기 직전에 카드의 세로 중심을 한 번 재어 변수로 넘긴다.
 */
export function createEnvelopeCeremony(host: HTMLElement, anchor: HTMLElement): EnvelopeCeremonyLayer {
  const root = createElement('div', 'ev-ceremony')
  root.setAttribute('aria-hidden', 'true')

  const dimNode = createElement('div', 'ev-ceremony__dim')
  const flare = createElement('div', 'ev-ceremony__flare')
  /*
   * 빛기둥과 충격파 링. **조각과 달리 인라인 값이 하나도 없다** - 자리도 시차도 CSS의 `nth-child`와
   * `--ev-burst-dur`에서 나오는 파생값이라 회차마다 달라질 것이 없다. 켜고 끄는 것은 `data-flare` 하나다.
   */
  const pillarBox = createElement('div', 'ev-ceremony__pillars')
  for (let i = 0; i < PILLAR_NODES; i += 1) pillarBox.append(createElement('span', 'ev-ceremony__pillar'))
  const ringBox = createElement('div', 'ev-ceremony__rings')
  for (let i = 0; i < RING_NODES; i += 1) ringBox.append(createElement('span', 'ev-ceremony__ring'))
  const flash = createElement('div', 'ev-ceremony__flash')
  const shardBox = createElement('div', 'ev-ceremony__shards')
  const shardNodes: HTMLElement[] = []
  for (let i = 0; i < SHARD_NODES; i += 1) {
    const shard = createElement('span', 'ev-ceremony__shard')
    shard.hidden = true
    shardBox.append(shard)
    shardNodes.push(shard)
  }
  // 순서가 곧 겹의 순서다. 기둥이 가장 뒤, 플래시가 가장 앞이다. 다만 그 "뒤"는 이 레이어 **안에서만**
  // 참이다 - 레이어 자체는 카드 위에 얹히므로, 기둥이 카드를 가르지 않게 뚫는 것은 CSS의 구멍이다.
  root.append(pillarBox, dimNode, flare, ringBox, flash, shardBox)

  /** 등급 색·빛의 종류를 CSS에 넘기는 자리. 클래스가 아니라 데이터 속성인 이유는 값이 열거형이라서다. */
  function markGrade(grade: Grade, plan: GradeCeremony): void {
    host.dataset.ceremony = grade
    root.dataset.flare = plan.flare
  }

  /** 길이를 CSS에 넘긴다. 표는 TS 한 곳에 있고 CSS는 변수를 읽기만 한다 - 값이 두 벌이 되지 않게. */
  function setDurations(plan: GradeCeremony): void {
    root.style.setProperty('--ev-dim-dur', `${plan.dimMs}ms`)
    root.style.setProperty('--ev-burst-dur', `${plan.tearMs + plan.stampMs}ms`)
  }

  /**
   * 빛과 조각의 중심을 카드에 맞춘다. 재는 것은 터뜨리기 직전 한 번뿐이라 매 프레임 비용이 아니다.
   * 레이아웃이 없는 환경(jsdom)에서는 높이가 0으로 나오므로 손대지 않고 CSS의 폴백(50%)에 맡긴다.
   */
  function aimAtCard(): void {
    const box = anchor.getBoundingClientRect()
    const frame = host.getBoundingClientRect()
    if (box.height <= 0) {
      root.style.removeProperty('--ev-ceremony-cy')
      return
    }
    root.style.setProperty('--ev-ceremony-cy', `${Math.round(box.top - frame.top + box.height / 2)}px`)
  }

  return {
    root,

    reset(): void {
      delete host.dataset.ceremony
      host.classList.remove('is-dim', 'is-burst', 'is-shake', 'is-settled')
      delete root.dataset.flare
      root.style.removeProperty('--ev-dim-dur')
      root.style.removeProperty('--ev-burst-dur')
      root.style.removeProperty('--ev-ceremony-cy')
      for (const shard of shardNodes) shard.hidden = true
    },

    dim(plan: GradeCeremony): void {
      // 등급은 아직 심지 않는다. 어두워지는 것 자체는 "무언가 크다"이지 "SSR이다"가 아니다 -
      // 색이 먼저 깔리면 어둠이 예고가 되고, 예고는 이 화면이 하지 않기로 한 것이다. SR도 어두워지므로
      // (P5) 어둠 하나로는 두 등급이 갈리지도 않는다 - 갈리는 것은 그 **길이**이고, 길이는 눈으로 못 센다.
      setDurations(plan)
      aimAtCard()
      host.classList.add('is-dim')
    },

    burst(grade: Grade, plan: GradeCeremony, shards: readonly Shard[]): void {
      markGrade(grade, plan)
      setDurations(plan)
      aimAtCard()
      host.classList.add('is-burst')
      if (plan.shake) host.classList.add('is-shake')

      shardNodes.forEach((node, index) => {
        const shard = shards[index]
        node.hidden = shard === undefined
        if (shard === undefined) return
        node.style.setProperty('--ev-sx', `${shard.x}px`)
        node.style.setProperty('--ev-sa', `${shard.apexY}px`)
        node.style.setProperty('--ev-sy', `${shard.y}px`)
        node.style.setProperty('--ev-sr', `${shard.rotate}deg`)
        node.style.setProperty('--ev-sd', `${shard.delayMs}ms`)
        node.style.setProperty('--ev-ss', shard.scale.toFixed(2))
      })
    },

    settle(grade: Grade, plan: GradeCeremony): void {
      // 건너뛰기·움직임 줄이기의 끝 상태. 등급 색은 남기고 **아무것도 움직이지 않는다** -
      // 기다리지 않기로 한 사람에게 뒤늦은 폭죽을 던지면 그건 건너뛴 것이 아니다.
      // `is-burst`를 걷는 한 줄이 SSR의 지속 오라까지 함께 끈다(오라는 그 클래스에 매달려 있다).
      markGrade(grade, plan)
      host.classList.remove('is-dim', 'is-burst', 'is-shake')
      host.classList.add('is-settled')
      for (const shard of shardNodes) shard.hidden = true
    },
  }
}
